/**
 * Utility e tipi per i campi telefono/cellulare.
 *
 * L'elenco dei prefissi internazionali non e' piu' hardcoded: viene recuperato
 * dal backend (tabella `dropdown_fields`, form/field `PHONE_PREFIX`) tramite
 * `FormDropdownService.getPhonePrefixes()`. Le funzioni di parsing che hanno
 * bisogno dell'elenco dei codici lo ricevono come parametro.
 */
export interface PhonePrefixOption {
  /** Prefisso internazionale comprensivo di "+" (es. "+39"). */
  code: string;
  /** Nome del paese mostrato nella tendina. */
  country: string;
  /** Etichetta completa usata per la ricerca e la visualizzazione. */
  label: string;
}

export const DEFAULT_PHONE_PREFIX = '+39';

export const MIN_PHONE_DIGITS = 6;
export const MAX_PHONE_DIGITS = 15;

/**
 * Separa un numero salvato (prefisso + numero in un'unica stringa) nelle due componenti.
 * Il riconoscimento del prefisso usa l'elenco di codici passato dal chiamante
 * (recuperato dal BE); se non viene riconosciuto alcun prefisso si usa quello italiano.
 */
export function splitPhoneNumber(
  value: string | null | undefined,
  prefixCodes: readonly string[] = [],
): {
  prefix: string;
  number: string;
} {
  const normalized = (value ?? '').replace(/\s+/g, '');
  if (!normalized) {
    return { prefix: DEFAULT_PHONE_PREFIX, number: '' };
  }

  // Ordiniamo dal prefisso piu' lungo al piu' corto per un match corretto.
  const byLengthDesc = [...prefixCodes].sort((a, b) => b.length - a.length);
  const matchedPrefix = byLengthDesc.find((prefix) => normalized.startsWith(prefix));
  if (matchedPrefix) {
    return { prefix: matchedPrefix, number: normalized.slice(matchedPrefix.length) };
  }

  return { prefix: DEFAULT_PHONE_PREFIX, number: normalized.replace(/^\+/, '') };
}

/** Unisce prefisso e numero in un'unica stringa, come richiesto dal formato di salvataggio. */
export function joinPhoneNumber(prefix: string, number: string): string {
  const cleanNumber = (number ?? '').replace(/\s+/g, '');
  if (!cleanNumber) {
    return '';
  }
  return `${prefix || DEFAULT_PHONE_PREFIX}${cleanNumber}`;
}

/** True se la parte nazionale contiene solo cifre (il prefisso non viene conteggiato). */
export function isPhoneNumberDigitsOnly(nationalNumber: string | null | undefined): boolean {
  const value = (nationalNumber ?? '').replace(/\s+/g, '');
  return value === '' || /^[0-9]+$/.test(value);
}

/** Valida la parte nazionale: solo cifre, lunghezza compresa tra 6 e 15. */
export function isValidNationalPhoneNumber(nationalNumber: string | null | undefined): boolean {
  const value = (nationalNumber ?? '').replace(/\s+/g, '');
  return (
    /^[0-9]+$/.test(value) && value.length >= MIN_PHONE_DIGITS && value.length <= MAX_PHONE_DIGITS
  );
}

/** Valida un numero completo (prefisso + numero) gia' concatenato. */
export function isValidFullPhoneNumber(
  value: string | null | undefined,
  prefixCodes: readonly string[] = [],
): boolean {
  return isValidNationalPhoneNumber(splitPhoneNumber(value, prefixCodes).number);
}
